import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import CollapsibleCard from './CollapsibleCard';
import { AlertIcon, GlobeIcon } from '../icons';
import type { WebSearchResultsPart } from './types';

const MAX_PREVIEW_CHIPS = 4;

const hostnameOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const isWebLink = (url: string) => /^https?:\/\//i.test(url);

// A monogram instead of a fetched favicon: rendering results must not call out to
// third-party sites or a favicon service from a corporate browser.
export const SiteMonogram: React.FC<{ hostname: string }> = ({ hostname }) => {
  const hue = Array.from(hostname).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 360, 0);

  return (
    <span
      className="cv-favicon cv-favicon--fallback"
      style={{ background: `hsl(${hue} 40% 46%)`, color: '#fff', fontSize: '10px', fontWeight: 600 }}
    >
      {(hostname[0] || '?').toUpperCase()}
    </span>
  );
};

const WebSearchResults: React.FC<{ data: WebSearchResultsPart }> = ({ data }) => {
  const { colors } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  if (data.search_status === 'blocked') {
    return (
      <CollapsibleCard icon={<AlertIcon size={16} />} title="Web search blocked">
        <div style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: '13px', color: colors.textPrimary, lineHeight: '1.5', marginBottom: '6px' }}>
            The search request was blocked because it may have contained sensitive information
            such as personally identifiable information (PII) or restricted content.
          </div>
          <div style={{ fontSize: '12px', color: colors.textMuted }}>
            Try rephrasing your search without using personal or sensitive information.
          </div>
        </div>
      </CollapsibleCard>
    );
  }

  if (data.results.length === 0) {
    return null;
  }

  const results = data.results.map((result) => ({ ...result, hostname: hostnameOf(result.url) }));
  const hiddenCount = results.length - MAX_PREVIEW_CHIPS;

  const preview = (
    <div className="cv-source-chips">
      {results.slice(0, MAX_PREVIEW_CHIPS).map((result, index) => (
        isWebLink(result.url) ? (
          <a
            key={index}
            className="cv-source-chip"
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            title={result.title}
          >
            <SiteMonogram hostname={result.hostname} />
            <span className="cv-source-chip__label">{result.hostname || result.title}</span>
          </a>
        ) : (
          <span key={index} className="cv-source-chip" title={result.title}>
            <SiteMonogram hostname={result.hostname} />
            <span className="cv-source-chip__label">{result.hostname || result.title}</span>
          </span>
        )
      ))}
      {hiddenCount > 0 && (
        <button type="button" className="cv-source-chip cv-source-chip--more" onClick={() => setIsExpanded(true)}>
          +{hiddenCount} more
        </button>
      )}
    </div>
  );

  return (
    <CollapsibleCard
      icon={<GlobeIcon size={16} />}
      title="Web sources"
      expanded={isExpanded}
      onExpandedChange={setIsExpanded}
      badges={<>
        <span className="cv-badge">{results.length}</span>
        {data.search_status === 'redacted' && <span className="cv-badge cv-badge--warning">Query modified</span>}
      </>}
      preview={preview}
    >
      {data.search_status === 'redacted' && data.original_query && (
        <div style={{
          padding: '10px 14px',
          backgroundColor: colors.surfaceElevated,
          borderBottom: `1px solid ${colors.borderLight}`,
          fontSize: '13px',
          color: colors.textPrimary,
          lineHeight: '1.5',
        }}>
          <div style={{ marginBottom: '4px', fontSize: '12px', color: colors.textMuted }}>
            Your search query was modified to remove sensitive information.
          </div>
          <div><strong>Original:</strong> {data.original_query}</div>
          {data.modified_query && (
            <div style={{ marginTop: '2px' }}><strong>Modified:</strong> {data.modified_query}</div>
          )}
        </div>
      )}

      {results.map((result, index) => (
        <div key={index} className="cv-list-item">
          <SiteMonogram hostname={result.hostname} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {isWebLink(result.url) ? (
              <a href={result.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500, fontSize: '14px' }}>
                {result.title}
              </a>
            ) : (
              <span style={{ fontWeight: 500, fontSize: '14px', color: colors.textPrimary }}>{result.title}</span>
            )}
            <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '1px' }}>{result.hostname}</div>
            {result.content && (
              <div className="cv-clamp-3" style={{ fontSize: '13px', color: colors.textSecondary, lineHeight: '1.45', marginTop: '4px' }}>
                {result.content}
              </div>
            )}
          </div>
        </div>
      ))}
    </CollapsibleCard>
  );
};

export default WebSearchResults;
